// account-history.component.ts
import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MaterialModule } from '../../material.module';
import { FormConfigService } from '../forms-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStateService } from '../../app-state.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { PaymentService } from '../payment-center/payment.service';
import { Router } from '@angular/router';
import { StatementPdfGenerator } from './statement-pdf-generator.service';

@Component({
  selector: 'app-account-history',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule
  ],
  templateUrl: './account-history.component.html',
  styleUrls: ['./account-history.component.scss']
})
export class AccountHistoryComponent implements OnInit {
  // Display columns for the desktop table view
  displayedColumns: string[] = ['date', 'description', 'amount', 'balance'];
  
  // Data properties
  accountHistory: any[] = [];
  accountAgingBalance: any = {};
  filteredAccountHistory: any[] = [];
  hasAccountHistory: boolean = true;
  loading = true;
  
  // Grouped data properties
  groupedTransactions: any = {};
  
  // Responsive design properties
  isMobile = false;
  
  // Form control for search
  searchControl = new FormControl('');
  
  // Service mapping for friendly descriptions
  serviceDescriptions: { [key: string]: string } = {
    // Standard services
    'OV1': 'Office Visit - New Patient',
    'OV2': 'Office Visit - Established Patient',
    'LAB': 'Laboratory Services',
    'RAD': 'Radiology',
    'PROC': 'Medical Procedure',
    'PAY': 'Payment Received',
    'INS': 'Insurance Payment',
    'T1356': 'Medical Imaging',
    'T1546': 'Lab Test - Standard',
    'T6357': 'Specialist Consultation',
    'Ins Pay Est': 'Estimated Insurance Payment',
    
    // Patient payments
    'PatPay': 'Payment Received',
    
    // Insurance payments
    'InsPay': 'Insurance Payment',
    'W/O': 'Insurance Write-Off',
    
    // Adjustments
    'Adj- Discount': 'Discount Applied',
    'Adj- Finance Charge': 'Finance Charge',
    'Adj- Insurance Adjustment': 'Insurance Adjustment',
    'Adj- Write Off': 'Write Off'
  };

  procedureCodes: { [key: string]: any } = {};

  
  // Inject services
  private snackBar = inject(MatSnackBar);
  private formConfigService = inject(FormConfigService);
  private appStateService = inject(AppStateService);
  private breakpointObserver = inject(BreakpointObserver);
  private paymentService = inject(PaymentService);
  private router = inject(Router);
  appState = inject(AppStateService);
  
  // Patient and client identifiers
  patientId: string;
  clientId: string;
  patientInfo: any = {};
  clinicInfo: any = {
    displayName: '',
    address: '',
    phoneNumber: ''
  };

  constructor() {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
    this.clinicInfo = this.appStateService.getState().clinicInfo || {
      displayName: '',
      address: '',
      phoneNumber: ''
    };
  }

  ngOnInit(): void {
    // Monitor screen size for responsive design
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet
    ]).subscribe(result => {
      this.isMobile = result.matches;
    });

    // Fetch data
    this.fetchProcedureCodes();
    this.fetchPatientInfo();
    this.fetchAccountHistory();
    this.fetchAccountAgingBalance();
    
    // Setup search filtering
    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe(value => {
        this.filterAccountHistory(value);
      });
  }

  gotoDashboard() {
    this.appState.setState({ appointmentData: null });
    this.router.navigate(['/dashboard', 'main']);
  }

  /**
   * Get icon name based on transaction source and type, accounting for major/minor procedures
   */
  getTransactionIcon(transaction: any): string {
    if (transaction.transactionSource === 'adjustment') {
      return transaction.transactionType === 'Charge' ? 'trending_up' : 'trending_down';
    } else if (transaction.transactionSource === 'insurance') {
      return 'health_and_safety';
    } else if (transaction.transactionSource === 'payment') {
      return 'payments';
    } else {
      return transaction.transactionType === 'Charge' ? 'add_circle' : 'remove_circle';
    }
  }

  /**
   * Get readable label for transaction source
   */
  getSourceLabel(source: string): string {
    const sourceMap: { [key: string]: string } = {
      'service': 'Service',
      'adjustment': 'Adjustment',
      'insurance': 'Insurance',
      'payment': 'Payment',
      'other': 'Other'
    };
    
    return sourceMap[source] || 'Service';
  }

  /**
   * Fetch patient information
   */
  fetchPatientInfo(): void {
    this.formConfigService.fetchPatient(this.clientId, this.patientId).subscribe({
      next: (data: any) => {
        this.patientInfo = data;
      },
      error: (error) => {
      }
    });
  }

  fetchProcedureCodes(): void {
    this.formConfigService.fetchProcedureCodes(this.clientId).subscribe({
      next: (data: any) => {
        this.procedureCodes = data;
      },
      error: (error) => {
      }
    });
  }


  getServiceDescription(code: string): string {
    // First check if this is a dental code in our procedureCodes mapping
    if (this.procedureCodes && this.procedureCodes[code]) {
      // Return the category (e.g., "Endo", "Fillings", "Cleanings")
      return this.procedureCodes[code].category;
    }
    
    // Fall back to standard service descriptions for non-dental codes
    return this.serviceDescriptions[code] || `${code}`;
  }

  /**
   * Fetch account history from the service
   */
  fetchAccountHistory(): void {
    this.loading = true;
    this.paymentService.fetchAccountHistory(this.clientId, this.patientId).subscribe({
      next: (data: any) => {
        // Format the data and filter out zero-value credit transactions
        this.accountHistory = data
          .map((item: any) => this.formatAccountData(item))
          .filter((item: any) => {
            // Filter out ALL transactions with both charge and credit of 0
            if (parseFloat(item.charge) === 0 && parseFloat(item.credit) === 0) {
              return false;
            }
            
            // Keep your existing filter for insurance credits
            if (item.transactionType === 'Credit' && 
                item.transactionSource === 'insurance' &&
                (parseFloat(item.credit) === 0 || 
                (item.date && item.date.getFullYear() <= 1900))) {
              return false;
            }
            
            return true;
          })
          .sort((a: any, b: any) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
        
        this.filteredAccountHistory = [...this.accountHistory];
        this.hasAccountHistory = this.accountHistory.length > 0;
        this.groupTransactionsByDate();
        this.loading = false;
      },
      error: (error) => {
        this.showNotification('Unable to load visit history. Please try again later.', 'error');
        this.hasAccountHistory = false;
        this.loading = false;
      }
    });
  }

  /**
   * Fetch account balance information
   */
  fetchAccountAgingBalance(): void {
    this.paymentService.fetchAccountAgingBalances(this.clientId, this.patientId).subscribe({
      next: (data: any) => {
        this.accountAgingBalance = this.formatAccountAgingBalanceData(data);
      },
      error: (error) => {
        this.showNotification('Unable to load account balance. Please try again later.', 'error');
      }
    });
  }

  /**
   * Format account transaction data
   */
  formatAccountData(data: any): any {
    let dateObj = null;
    if (data.date) {
      const parsedDate = new Date(data.date);
      if (!isNaN(parsedDate.getTime())) {
        dateObj = parsedDate;
      }
    }
    
    // Determine transaction source based on code pattern
    let transactionSource = 'service'; // Default
    if (data.code) {
      if (data.code.startsWith('Adj-')) {
        transactionSource = 'adjustment';
      } else if (['Ins Pay Est', 'InsPay', 'W/O'].includes(data.code)) {
        transactionSource = 'insurance';
      } else if (['PAY', 'PatPay'].includes(data.code)) {
        transactionSource = 'payment';
      }
    }
    
    // Use source field from backend if available
    if (data.source) {
      transactionSource = data.source;
    }
    
    return {
      date: dateObj,
      dateStr: data.date, // Keep original date string for grouping
      patientName: data.patientName || 'Not provided',
      code: data.code, // Keep original code for display
      charge: data.transactionType === 'Charge' ? (data.charge || 0) : 0,
      credit: data.transactionType === 'Credit' ? (data.credit || 0) : 0,
      balance: data.balance || 0.00,
      transactionType: data.transactionType || 'Unknown',
      transactionSource: transactionSource // Add transaction source
    };
  }

  /**
   * Format account balance data
   */
  formatAccountAgingBalanceData(data: any): any {
    return {
      Bal_0_30: data.Bal_0_30 || 0.00,
      Bal_31_60: data.Bal_31_60 || 0.00,
      Bal_61_90: data.Bal_61_90 || 0.00,
      BalOver90: data.BalOver90 || 0.00,
      Total: data.Total || 0.00,
      InsEst: data.InsEst || 0.00,
      EstBal: data.EstBal || 0.00,
      PatEstBal: data.PatEstBal || 0.00,
      Unearned: data.Unearned || 0.00
    };
  }

  /**
   * Group transactions by date
   */
  groupTransactionsByDate(): void {
    const grouped: any = {};
    
    // Filter out transactions with invalid dates
    const validTransactions = this.filteredAccountHistory.filter(t => 
      t.date && t.date.getFullYear() > 1900
    );
    
    // Group by date
    validTransactions.forEach(transaction => {
      const dateKey = this.formatDateKey(transaction.date);
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: transaction.date,
          dateStr: dateKey,
          charges: [],
          payments: [],
          dailyChargesTotal: 0,
          dailyPaymentsTotal: 0,
          endingBalance: transaction.balance,
          allTransactions: [] // Add a new property for combined transactions
        };
      }
      
      // Add to appropriate category
      if (transaction.transactionType === "Charge") {
        grouped[dateKey].charges.push(transaction);
        grouped[dateKey].dailyChargesTotal += parseFloat(transaction.charge || 0);
      } else {
        grouped[dateKey].payments.push(transaction);
        grouped[dateKey].dailyPaymentsTotal += parseFloat(transaction.credit || 0);
      }
      
      // Add to combined transactions list
      grouped[dateKey].allTransactions.push(transaction);
      
      // Always use the latest balance as the ending balance
      grouped[dateKey].endingBalance = transaction.balance;
    });
    
    // Sort the combined transactions list for each group
    Object.values(grouped).forEach((group: any) => {
      group.allTransactions.sort((a: any, b: any) => {
        // First sort by transaction type (Charges first, then Credits)
        if (a.transactionType !== b.transactionType) {
          return a.transactionType === 'Charge' ? -1 : 1;
        }
        // Then sort by code
        return a.code.localeCompare(b.code);
      });
    });
    
    // Convert to array and sort by date (newest first)
    this.groupedTransactions = Object.values(grouped).sort((a: any, b: any) => 
      b.date.getTime() - a.date.getTime()
    );
  }

  /**
   * Format date to use as grouping key
   */
  formatDateKey(date: Date): string {
    // Use local timezone to avoid timezone offset issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date for display
   */
  formatDateForDisplay(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  }

  /**
   * Filter account history based on search term
   */
  filterAccountHistory(searchTerm: string | null): void {
    const term = searchTerm ? searchTerm.trim().toLowerCase() : '';
    if (!term) {
      this.filteredAccountHistory = [...this.accountHistory];
    } else {
      this.filteredAccountHistory = this.accountHistory.filter(entry => {
        const formattedDate = entry.date
          ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
              .format(entry.date).toLowerCase()
          : '';
        
        // Get the friendly service description
        const serviceDesc = this.getServiceDescription(entry.code).toLowerCase();
        
        // Also include transaction type (like 'charge' or 'credit')
        const transactionType = entry.transactionType ? entry.transactionType.toLowerCase() : '';

        // And include the source label for completeness
        const sourceLabel = this.getSourceLabel(entry.transactionSource).toLowerCase();

        // Check if any of the fields include the search term
        return formattedDate.includes(term) ||
              entry.code.toLowerCase().includes(term) ||
              serviceDesc.includes(term) ||
              transactionType.includes(term) ||
              sourceLabel.includes(term) ||
              entry.charge.toString().includes(term) ||
              entry.credit.toString().includes(term);
      });
    }
    
    // Re-group filtered transactions
    this.groupTransactionsByDate();
  }

  /**
   * Display notification to user
   */
  showNotification(message: string, type: 'info' | 'error' = 'info'): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: type === 'error' ? ['error-snackbar'] : ['info-snackbar']
    });
  }

  /**
   * Handle payment form submission via Angular Router navigation
   */
  submitPaymentForm(): void {
    this.router.navigate(['/dashboard', 'payments']);
  }

  /**
   * Generate PDF statement
   */
  generatePdf(): void {
    this.showNotification('Preparing your statement for download...');
    
    try {
      // Check if necessary data is available
      if (!this.accountHistory || this.accountHistory.length === 0) {
        throw new Error('No account history data available');
      }
      
      
      // Use the StatementPdfGenerator service to generate the PDF
      StatementPdfGenerator.generateStatement(
        this.accountHistory,
        this.patientInfo,
        this.accountAgingBalance,
        this.clinicInfo  // Pass clinic information
      );
      
    } catch (error) {
      this.showNotification('Error generating statement. Please try again later.', 'error');
    }
  }
}
